/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import apiService from './api.jsx';
import { jwtDecode } from 'jwt-decode';
import googleAuthService from './googleAuthService.jsx';
import {
  clearAllStorageData,
  getAuthData,
  getStoredAuthData,
  setStoredAuthData
} from '../utils/authStorage.js';

class AuthService {
  buildAuthData(response, fallbackUser = {}) {
    const decodedToken = jwtDecode(response.token);
    const role = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || fallbackUser.role;

    return {
      token: response.token,
      refreshToken: response.refreshToken,
      emailVerified:
        response.emailVerified ??
        response.isEmailVerified ??
        response.emailConfirmed ??
        response.isEmailConfirmed ??
        !response.message?.toLowerCase?.().includes('email verification required'),
      user: {
        id: decodedToken.sub || fallbackUser.id,
        email: decodedToken.email || fallbackUser.email,
        firstName: decodedToken.firstName || fallbackUser.firstName,
        lastName: decodedToken.lastName || fallbackUser.lastName,
        exp: decodedToken.exp,
        ...fallbackUser,
        role
      },
      isSuccess: response.isSuccess,
      message: response.message,
      userDataLastFetch: null
    };
  }

  hasPersonalInformation(user = {}) {
    return Boolean(user.firstName?.trim?.() && user.lastName?.trim?.() && user.phoneNumber?.trim?.());
  }

  hasUserAddress(user = {}) {
    const address = user.address || user.userAddress;
    return Boolean(
      address?.street ||
        address?.streetAddress ||
        address?.fullAddress ||
        user.street ||
        user.streetAddress ||
        user.fullAddress
    );
  }

  hasBusinessInformation(user = {}) {
    return Boolean(user.businessName || user.businessInfo || user.categoryId || user.artisanCategoryId);
  }

  isEmailVerified(user = {}, authData = {}) {
    return Boolean(
      user.emailVerified ??
        user.isEmailVerified ??
        user.emailConfirmed ??
        user.isEmailConfirmed ??
        authData.emailVerified ??
        authData.isEmailVerified ??
        authData.emailConfirmed ??
        authData.isEmailConfirmed
    );
  }

  getOnboardingStatus(authData = {}) {
    const user = authData.user || {};
    const role = user.role;
    const hasRequiredUserFields =
      this.isEmailVerified(user, authData) && this.hasPersonalInformation(user) && this.hasUserAddress(user);
    const isComplete = role === 'Artisan' ? hasRequiredUserFields && this.hasBusinessInformation(user) : hasRequiredUserFields;

    return {
      isComplete,
      emailVerified: this.isEmailVerified(user, authData),
      hasPersonalInformation: this.hasPersonalInformation(user),
      hasUserAddress: this.hasUserAddress(user),
      hasBusinessInformation: role === 'Artisan' ? this.hasBusinessInformation(user) : true
    };
  }

  persistAuthDataWhenOnboardingComplete(authData) {
    const onboardingStatus = this.getOnboardingStatus(authData);
    const nextAuthData = {
      ...authData,
      onboardingStatus
    };

    setStoredAuthData(nextAuthData);

    return nextAuthData;
  }

  getCurrentAuthData() {
    return getAuthData();
  }

  isOnboardingComplete() {
    const authData = getStoredAuthData();
    if (!authData.token || !authData.user) return false;

    return authData.onboardingStatus?.isComplete ?? this.getOnboardingStatus(authData).isComplete;
  }

  getRequiredOnboardingStep(authData = getStoredAuthData()) {
    const onboardingStatus = authData.onboardingStatus || this.getOnboardingStatus(authData);

    if (!authData.token || !authData.user || onboardingStatus.isComplete) return null;
    if (!onboardingStatus.emailVerified) return 'emailVerification';
    if (!onboardingStatus.hasPersonalInformation) return 'personalInfo';
    if (!onboardingStatus.hasUserAddress) return 'location';
    if (!onboardingStatus.hasBusinessInformation) return 'businessInfo';

    return null;
  }

  getRequiredOnboardingState(authData = getStoredAuthData()) {
    const step = this.getRequiredOnboardingStep(authData);
    if (!step) return null;

    return {
      step,
      email: authData.user?.email,
      role: authData.user?.role
    };
  }

  /**
   * Login user with email and password
   * @param {string} email
   * @param {string} password
   * @returns {Promise<object>} Authentication response
   */
  async login(email, password) {
    clearAllStorageData();

    const loginData = {
      email,
      password,
      platform: 'web'
    };

    const response = await apiService.post('/Auth/login', loginData);

    if (response.isSuccess) {
      // Check if 2FA is required (either by flag or message content)
      const is2FARequired =
        response.requiresTwoFactor ||
        (response.message && response.message.toLowerCase().includes('two-factor authentication required'));

      if (is2FARequired) {
        return {
          isSuccess: true,
          requiresTwoFactor: true,
          message: response.message || 'Two-factor authentication required',
          email: email,
          userId: response.userId || email, // Use userId if provided, otherwise use email
          tempToken: response.tempToken,
          userRole: response.userRole
        };
      }

      // Regular login success
      if (response.token) {
        let authData = this.buildAuthData(response);
        setStoredAuthData(authData);

        // Always fetch fresh user data after successful login
        try {
          await this.fetchCurrentUser();
          authData = getAuthData();
        } catch {
          // Don't block login flow if user data fetch fails
        }

        return this.persistAuthDataWhenOnboardingComplete(authData);
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } else {
      throw new Error(response.message || 'Login failed');
    }
  }

  /**
   * Logout user
   */
  async logout() {
    clearAllStorageData();
    return { success: true };
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    try {
      const authData = getStoredAuthData();

      if (!authData.token || !authData.user) {
        return false;
      }

      // Check if token is expired
      const currentTime = Date.now() / 1000;
      if (authData.user.exp && authData.user.exp < currentTime) {
        this.logout(); // Clear expired token
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current user data from localStorage
   * @returns {object|null}
   */
  getCurrentUser() {
    try {
      const authData = getStoredAuthData();
      return authData.user || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get current user data from either persisted auth or the in-memory onboarding session.
   * This keeps onboarding API calls working without persisting incomplete auth tokens.
   * @returns {object|null}
   */
  getCurrentSessionUser() {
    try {
      const authData = getAuthData();
      return authData.user || null;
    } catch {
      return null;
    }
  }

  /**
   * Fetch current user details from API
   * @returns {Promise<object>} User data from API
   */
  async fetchCurrentUser() {
    const response = await apiService.get('/Users/GetCurrentUser', { requireAuth: true });

    if (response.isSuccess && response.data) {
      // ALWAYS update stored user data with API response
      const authData = getAuthData();

      // Check if we have auth token (must exist for authenticated requests)
      if (authData.token) {
        // Merge API data with existing JWT data, API data takes precedence for profile info
        authData.user = {
          ...authData.user, // Keep JWT data like exp, id from token
          ...response.data, // Override with fresh API data
          role: response.data.role || authData.user?.role // Keep original role value from API
        };

        // Set timestamp for last fetch
        authData.userDataLastFetch = Date.now();

        this.persistAuthDataWhenOnboardingComplete(authData);

        return authData.user;
      } else {
        // Edge case: No auth token found (shouldn't happen in normal flow)
        return response.data;
      }
    } else {
      throw new Error(response.message || 'Failed to fetch user data');
    }
  }

  /**
   * Get enhanced user data (tries API first, falls back to localStorage)
   * @param {boolean} forceRefresh - Whether to force fetch from API
   * @returns {Promise<object|null>}
   */
  async getEnhancedUserData(forceRefresh = false) {
    try {
      if (forceRefresh || this.shouldRefreshUserData()) {
        return await this.fetchCurrentUser();
      }

      // Return cached data if recent enough
      return this.getCurrentUser();
    } catch (error) {
      // Fallback to cached data if API fails
      return this.getCurrentUser();
    }
  }

  /**
   * Check if user data should be refreshed
   * @returns {boolean}
   */
  shouldRefreshUserData() {
    try {
      const authData = getStoredAuthData();
      const lastFetch = authData.userDataLastFetch;

      if (!lastFetch) return true;

      // Refresh if data is older than 5 minutes
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      return lastFetch < fiveMinutesAgo;
    } catch (error) {
      return true;
    }
  }

  /**
   * Get authentication token
   * @returns {string|null}
   */
  getToken() {
    try {
      return getStoredAuthData().token || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get user role
   * @returns {string|null}
   */
  getUserRole() {
    try {
      const user = this.getCurrentUser();
      return user?.role || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Refresh authentication token
   * @returns {Promise<object>}
   */
  async refreshToken() {
    try {
      const authData = getStoredAuthData();

      if (!authData.refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.post('/Auth/refresh', {
        refreshToken: authData.refreshToken
      });

      if (response.isSuccess && response.token) {
        // Update stored token
        authData.token = response.token;
        if (response.refreshToken) {
          authData.refreshToken = response.refreshToken;
        }

        // Decode new token for updated user info
        const decodedToken = jwtDecode(response.token);
        authData.user.exp = decodedToken.exp;

        setStoredAuthData(authData);

        return authData;
      } else {
        throw new Error(response.message || 'Token refresh failed');
      }
    } catch (error) {
      this.logout(); // Clear invalid tokens
      throw error;
    }
  }

  /**
   * Register new user (if registration endpoint becomes available)
   * @param {object} userData
   * @returns {Promise<object>}
   */
  async register(userData) {
    try {
      const response = await apiService.post('/Auth/register', userData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Request password reset
   * @param {string} email
   * @returns {Promise<object>}
   */
  async forgotPassword(email) {
    try {
      const response = await apiService.post('/Auth/forgot-password', { email });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Validate reset password code
   * @param {string} email
   * @param {string} code
   * @returns {Promise<object>}
   */
  async validateResetCode(email, code) {
    try {
      const response = await apiService.post('/Auth/validate-reset-code', {
        email,
        code
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Resend reset password code
   * @param {string} email
   * @returns {Promise<object>}
   */
  async resendResetCode(email) {
    try {
      const response = await apiService.post('/Auth/resend-reset-code', { email });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reset password with email, code and new password
   * @param {string} email
   * @param {string} code
   * @param {string} newPassword
   * @returns {Promise<object>}
   */
  async resetPassword(email, code, newPassword) {
    try {
      const response = await apiService.post('/Auth/reset-password', {
        email,
        code,
        newPassword
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify two-factor authentication code
   * @param {string} userId
   * @param {string} token
   * @returns {Promise<object>}
   */
  async verifyTwoFactorAuth(userId, token) {
    try {
      // Prepare request data with correct parameters
      const requestData = {
        userId,
        token
      };

      // Use the correct 2FA verification endpoint
      const response = await apiService.post('/Auth/verify-2fa', requestData);

      if (response.isSuccess && response.token) {
        let authData = this.buildAuthData(response);
        setStoredAuthData(authData);

        // Always fetch fresh user data after successful 2FA verification
        try {
          await this.fetchCurrentUser();
          authData = getAuthData();
        } catch {
          // Don't block login flow if user data fetch fails
        }

        return this.persistAuthDataWhenOnboardingComplete(authData);
      } else {
        throw new Error(response.message || '2FA verification failed');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Resend two-factor authentication code
   * @param {string} email
   * @returns {Promise<object>}
   */
  async resendTwoFactorCode(email) {
    try {
      // Use the correct 2FA resend endpoint
      const response = await apiService.post('/Auth/resend-2fa', { email });

      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Login with Google credential
   * @param {string} credential - Google JWT credential
   * @param {string} role - User role ('User' or 'Artisan')
   * @returns {Promise<object>} Authentication response
   */
  async loginWithGoogle(credential, role) {
    try {
      clearAllStorageData();

      // Extract user info from Google credential
      const googleUserInfo = googleAuthService.extractUserInfo(credential);

      // Send Google credential to backend external login endpoint
      const requestBody = {
        provider: 'google',
        token: credential,
        password: '' // Not needed for Google OAuth
      };

      // Only include role if provided (for signup scenarios)
      if (role) {
        requestBody.role = role;
      }

      const response = await apiService.post('/Auth/external-login', requestBody);

      if (response.isSuccess && response.token) {
        let authData = this.buildAuthData(response, {
          email: googleUserInfo.email,
          firstName: googleUserInfo.firstName,
          lastName: googleUserInfo.lastName,
          picture: googleUserInfo.picture,
          provider: 'google'
        });
        setStoredAuthData(authData);

        // Always fetch fresh user data after successful Google login
        try {
          await this.fetchCurrentUser();
          authData = getAuthData();
        } catch {
          // Don't block login flow if user data fetch fails
        }

        return this.persistAuthDataWhenOnboardingComplete(authData);
      } else {
        throw new Error(response.message || 'Google login failed');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign in with Google using One Tap or popup
   * @param {string} role - User role ('User' or 'Artisan') - optional for existing users
   * @returns {Promise<object>} Authentication response
   */
  async signInWithGoogle(role) {
    try {
      // Try One Tap first, fall back to popup
      let googleResponse;

      try {
        googleResponse = await googleAuthService.signInWithOneTap();
      } catch (oneTapError) {
        googleResponse = await googleAuthService.signInWithPopup();
      }

      if (!googleResponse?.credential) {
        throw new Error('No credential received from Google');
      }

      // Authenticate with our backend
      return await this.loginWithGoogle(googleResponse.credential, role);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Initialize Google Sign-In
   */
  async initializeGoogleAuth() {
    try {
      await googleAuthService.initialize();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if Google Sign-In is available
   */
  isGoogleAuthAvailable() {
    return googleAuthService.isAvailable();
  }

  /**
   * Register with Google credential
   * @param {string} credential - Google JWT credential
   * @param {string} role - User role ('User' or 'Artisan')
   * @returns {Promise<object>} Registration response
   */
  async registerWithGoogle(credential, role) {
    try {
      // Extract user info from Google credential
      const googleUserInfo = googleAuthService.extractUserInfo(credential);

      // Send Google credential to backend external register endpoint
      const response = await apiService.post('/Auth/external-register', {
        provider: 'google',
        token: credential,
        password: '', // Not needed for Google OAuth
        role: role
      });

      if (response.isSuccess && response.token) {
        let authData = this.buildAuthData(response, {
          email: googleUserInfo.email,
          firstName: googleUserInfo.firstName,
          lastName: googleUserInfo.lastName,
          picture: googleUserInfo.picture,
          provider: 'google',
          role
        });
        setStoredAuthData(authData);

        // Always fetch fresh user data after successful Google registration
        try {
          await this.fetchCurrentUser();
          authData = getAuthData();
        } catch {
          // Don't block registration flow if user data fetch fails
        }

        return this.persistAuthDataWhenOnboardingComplete(authData);
      } else {
        throw new Error(response.message || 'Google registration failed');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign up with Google using One Tap or popup
   * @param {string} role - User role ('User' or 'Artisan')
   * @returns {Promise<object>} Registration response
   */
  async signUpWithGoogle(role = 'User') {
    try {
      // Try One Tap first, fall back to popup
      let googleResponse;

      try {
        googleResponse = await googleAuthService.signInWithOneTap();
      } catch (oneTapError) {
        googleResponse = await googleAuthService.signInWithPopup();
      }

      if (!googleResponse?.credential) {
        throw new Error('No credential received from Google');
      }

      // Register with our backend
      return await this.registerWithGoogle(googleResponse.credential, role);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Request OTP to be sent to user's email for verification
   * @param {string} email - User's email address
   * @returns {Promise<object>} Response from validate-email endpoint
   */
  async requestEmailVerificationOTP(email) {
    try {
      const response = await apiService.post('/Auth/validate-email', { email });
      return response;
    } catch (error) {
      throw error;
    }
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;
